import { useState, useEffect, Component } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import Sidebar from './layout/Sidebar';
import Topbar from './layout/Topbar';
import ChatAssistant from './ChatAssistant';
import SpotlightSearch from './SpotlightSearch';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import Breadcrumbs from './common/Breadcrumbs';
import { OnboardingChecklist } from './onboarding';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import CommandCenterLifecycleTracker from './operations/CommandCenterLifecycleTracker';

// Error boundary for layout components
class LayoutErrorBoundary extends Component {
  state = { hasError: false, errorInfo: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, errorInfo: String(error) };
  }
  componentDidCatch(error, info) {
    console.error('[Layout] Component crash:', error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return null; // Silently fail for layout widgets
    }
    return this.props.children;
  }
}

const Layout = () => {
  const location = useLocation();
  const { user } = useAuth();
  const tierOverride = user?.organization?.tierOverride;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const { toggleTheme } = useTheme();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Handle toggle - on mobile toggle menu, on desktop toggle collapse
  const handleSidebarToggle = () => {
    if (window.innerWidth < 768) {
      setMobileMenuOpen(prev => !prev);
    } else {
      setSidebarCollapsed(prev => !prev);
    }
  };

  // Global keyboard shortcuts
  useKeyboardShortcuts({
    onSearch: () => setSearchOpen(true),
    onToggleSidebar: handleSidebarToggle,
    onToggleTheme: toggleTheme,
    onShowHelp: () => setShortcutsHelpOpen(true),
  });

  return (
    <div className="h-screen bg-gray-50 dark:bg-dark-900 flex flex-col overflow-hidden">
      <CommandCenterLifecycleTracker />
      {/* Skip Navigation Link for Keyboard Users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-brand-500 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Top Navigation - Sticky */}
      <div className="sticky top-0 z-50">
        <Topbar
          onToggleSidebar={handleSidebarToggle}
          mobileMenuOpen={mobileMenuOpen}
          onCloseMobileMenu={() => setMobileMenuOpen(false)}
        />
        <Breadcrumbs />
      </div>

      {/* Sidebar + Main Content */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile overlay backdrop */}
        {mobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar - hidden on mobile unless menu open, always visible on desktop */}
        <div className={`
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
          fixed md:relative
          z-50 md:z-auto
          h-[calc(100vh-4rem)] md:h-auto
          pb-[env(safe-area-inset-bottom,0px)] md:pb-0
          transition-transform duration-300 ease-in-out
        `}>
          <Sidebar collapsed={sidebarCollapsed} onClose={() => setMobileMenuOpen(false)} />
        </div>

        <main id="main-content" className="flex-1 overflow-y-auto bg-gray-50 dark:bg-dark-800" role="main" aria-label="Main content">
          {tierOverride?.active && (
            <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700 px-4 py-2 flex items-center justify-center gap-2 text-sm text-amber-800 dark:text-amber-300">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>
                <strong>Tier Override Active:</strong> Testing <strong>{tierOverride.effectiveTier}</strong> tier (real tier: {tierOverride.realTier})
              </span>
            </div>
          )}
          <div className="p-4 md:p-6">
            {/* Onboarding Checklist - shows until dismissed */}
            <LayoutErrorBoundary>
              <OnboardingChecklist variant="card" className="mb-6" />
            </LayoutErrorBoundary>

            <Outlet />
          </div>

          {/* Footer */}
          <footer className="px-4 md:px-6 py-4 mt-auto border-t border-gray-200 dark:border-dark-700">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-400 dark:text-gray-500">
              <span>&copy; {new Date().getFullYear()} Gabhan Investments LLC</span>
              <div className="flex gap-3">
                <Link to="/privacy" className="hover:text-gray-600 dark:hover:text-gray-300">Privacy</Link>
                <Link to="/terms" className="hover:text-gray-600 dark:hover:text-gray-300">Terms</Link>
              </div>
            </div>
          </footer>
        </main>
      </div>

      {/* AI Chat Assistant - Floating Button */}
      <LayoutErrorBoundary>
        <ChatAssistant />
      </LayoutErrorBoundary>

      {/* Spotlight Search - Global (Ctrl+K) */}
      <SpotlightSearch open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Keyboard Shortcuts Help - Global (Ctrl+/) */}
      <KeyboardShortcutsHelp open={shortcutsHelpOpen} onClose={() => setShortcutsHelpOpen(false)} />
    </div>
  );
};

export default Layout;
